import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getWishlist, removeFromWishlist } from '../api/wishlist';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { WishlistItem } from '../types';

const WishlistPage: React.FC = () => {
  const { user, openLogin } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWishlist = async () => {
    setLoading(true);
    const data = await getWishlist();
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchWishlist();
    else setLoading(false);
  }, [user]);

  if (!user) {
    return (
      <div className="cart-page">
        <div className="empty-state">
          <div className="empty-state__icon">♡</div>
          <h2>Your wishlist is empty</h2>
          <p>Sign in to save your favourite eyewear.</p>
          <button className="btn btn--primary" onClick={() => openLogin()}>Sign In</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cart-page">
        <div className="products-grid">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton-card" />)}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-state">
          <div className="empty-state__icon">♡</div>
          <h2>Your wishlist is empty</h2>
          <p>Save items you love and come back to them anytime.</p>
          <Link to="/products" className="btn btn--primary">Browse Products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <span>Wishlist</span>
      </div>

      <h1 className="cart-page__title">My Wishlist <span className="cart-page__count">{items.length} items</span></h1>

      <div className="wishlist-grid">
        {items.map((item) => {
          const image = item.images?.[0] || '';
          return (
            <div key={item.wishlist_id} className="product-card">
              <div className="product-card__image-wrap">
                <img
                  src={image}
                  alt={item.product_name}
                  className="product-card__image"
                  onClick={() => navigate(`/product/${item.product_id}`)}
                />
                <button
                  className="product-card__wish-btn active"
                  onClick={async () => {
                    await removeFromWishlist(item.wishlist_id);
                    setItems((prev) => prev.filter((i) => i.wishlist_id !== item.wishlist_id));
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>
              </div>
              <div className="product-card__info">
                {item.brand && <p className="product-card__brand">{item.brand}</p>}
                <h3 className="product-card__name">{item.product_name}</h3>
                <div className="product-card__price-row">
                  <span className="product-card__price">₹{Math.round(item.selling_price).toLocaleString()}</span>
                </div>
                <button
                  className="product-card__cart-btn"
                  onClick={async () => {
                    await addToCart(item.product_id, 1);
                  }}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WishlistPage;
